import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { RUTAS } from '@/api/rutas';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';

interface Desafio {
  svg: string;
  token: string;
}

/**
 * La cuenta que hay que resolver antes de mandar un formulario.
 *
 * Es la misma que pide el inicio de sesión, y el motivo de que exista en el formulario de contacto
 * es que ése es el único lugar público del sitio donde un desconocido escribe algo que después lee
 * una persona. Sin esto, la bandeja se llena de robots en una semana.
 *
 * Devuelve lo que hace falta para mandar —el token y la respuesta escrita— y el control ya
 * dibujado. `refrescar` pide otra cuenta: un token vale una sola vez, así que después de un
 * intento fallido hay que pedir uno nuevo o el segundo intento falla por el motivo equivocado.
 */
export function useCaptcha(): {
  token: string | null;
  respuesta: string;
  listo: boolean;
  refrescar: () => void;
  control: ReactNode;
} {
  const [desafio, setDesafio] = useState<Desafio | null>(null);
  const [respuesta, setRespuesta] = useState('');
  const [cargando, setCargando] = useState(true);

  const pedir = useCallback(async () => {
    setCargando(true);
    setRespuesta('');

    try {
      const respuestaHttp = await fetch(RUTAS.sesion.captcha);
      setDesafio(respuestaHttp.ok ? ((await respuestaHttp.json()) as Desafio) : null);
    } catch {
      setDesafio(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void pedir();
  }, [pedir]);

  const control = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Resolvé la cuenta para confirmar que no sos un robot.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 150,
            minHeight: 60,
            px: 1,
            bgcolor: 'rgba(255,255,255,.06)',
            borderRadius: 1,
          }}
        >
          {cargando && <CircularProgress size={24} aria-label="Cargando la cuenta" />}

          {!cargando && desafio && (
            <Box
              component="img"
              src={`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(desafio.svg)))}`}
              alt="Cuenta a resolver"
              sx={{ maxHeight: 60 }}
            />
          )}

          {!cargando && !desafio && (
            <Typography variant="caption" color="text.secondary">
              No se pudo cargar la verificación.
            </Typography>
          )}
        </Box>

        <Tooltip title="Otra cuenta">
          <span>
            <IconButton
              onClick={() => void pedir()}
              disabled={cargando}
              aria-label="Generar otra cuenta"
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          label="Resultado"
          value={respuesta}
          onChange={evento => setRespuesta(evento.target.value)}
          required
          inputMode="numeric"
          sx={{ maxWidth: 140 }}
        />
      </Box>
    </Box>
  );

  return {
    token: desafio?.token ?? null,
    respuesta,
    listo: desafio !== null && respuesta.trim() !== '',
    refrescar: () => void pedir(),
    control,
  };
}
